import Cropper from 'cropperjs';
import { useCallback } from 'react';

interface UseCropSaveHandlerOptions {
    cropperRef: React.RefObject<Cropper>;
    isDirtyRef: React.MutableRefObject<boolean>;
    element: any;
    elementId: string;
    imageWidth: number;
    imageHeight: number;
    imageType: string;
    largestUneditedImageUrl: string;
    currentRotation: number;
    hideDrawing: boolean;
    afterSaveCb: () => void;
    updateElementImageData: (
        elementId: string,
        imageOriginal: any,
        imageEditorData: any,
        clearDrawing: boolean,
    ) => void;
    revertToOriginalImageData: (elementId: string, imageOriginal: any, clearDrawing: boolean) => void;
    uploadEditedImage: (elementId: string, file: Blob & { name: string; lastModified: number }) => void;
    getOriginalImage: (element: any) => any;
    getImageProp: (element: any) => any;
    getFilenameFromUrl: (url: string, type: string) => string;
    appendToFilename: (filename: string) => string;
    inRange: (min: number, max: number) => (value: number) => boolean;
}

export function useCropSaveHandler({
    cropperRef,
    isDirtyRef,
    element,
    elementId,
    imageWidth,
    imageHeight,
    imageType,
    largestUneditedImageUrl,
    currentRotation,
    hideDrawing,
    afterSaveCb,
    updateElementImageData,
    revertToOriginalImageData,
    uploadEditedImage,
    getOriginalImage,
    getImageProp,
    getFilenameFromUrl,
    appendToFilename,
    inRange,
}: UseCropSaveHandlerOptions) {
    const handleSave = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        // If no changes were made, just exit the editor
        if (!isDirtyRef.current) {
            return afterSaveCb();
        }

        const imageData = cropper.getImageData();
        const imageEditorData = cropper.getData();
        const originalImage = getOriginalImage(element);

        const inWidthRange = inRange(
            imageData.naturalWidth - imageData.naturalWidth * 0.01,
            imageData.naturalWidth - imageData.naturalWidth * 0.01,
        );

        const inHeightRange = inRange(
            imageData.naturalHeight - imageData.naturalHeight * 0.01,
            imageData.naturalHeight + imageData.naturalHeight * 0.01,
        );

        const shouldRevertToOriginal =
            originalImage &&
            imageEditorData.rotate === 0 &&
            inWidthRange(imageEditorData.width) &&
            inHeightRange(imageEditorData.height);

        // If the image has returned to an uncropped, unrotated state, replace the edited image with the original
        if (shouldRevertToOriginal) {
            revertToOriginalImageData(elementId, originalImage.toJS(), hideDrawing);

            return afterSaveCb();
        }

        const blobCreatedCb = (blob: Blob) => {
            const imageOriginalData = getOriginalImage(element) || getImageProp(element);

            updateElementImageData(elementId, imageOriginalData, imageEditorData, hideDrawing);

            // Create extra data requited to parse Blob into a File
            const originalFilename = getFilenameFromUrl(largestUneditedImageUrl, imageType);
            const imageFilename = appendToFilename(originalFilename);

            const enrichedBlob = Object.assign(blob, {
                name: imageFilename,
                lastModified: Date.now(),
            }) as Blob & { name: string; lastModified: number };

            uploadEditedImage(elementId, enrichedBlob);

            afterSaveCb();
        };

        cropper
            .getCroppedCanvas({
                width: imageEditorData.width,
                height: imageEditorData.height,
                maxWidth: 4096,
                maxHeight: 4096,
            })
            .toBlob(
                (blob) => {
                    if (!blob) return;
                    blobCreatedCb(blob);
                },
                imageType,
                0.85,
            );
    }, [
        cropperRef,
        isDirtyRef,
        element,
        elementId,
        imageWidth,
        imageHeight,
        imageType,
        largestUneditedImageUrl,
        currentRotation,
        hideDrawing,
        afterSaveCb,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        getOriginalImage,
        getImageProp,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
    ]);

    return { handleSave };
}
